import { TrackingPageTypeWithoutId } from '@aboutyou/legacy-tracking-sdk'
import PromotionLink from 'components/PromotionLink'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { getImageUrl } from 'helpers/image'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { OutfitPromotionTracker } from 'helpers/tracking/trackerComponents/ElementTracker'
import { useObserver } from 'mobx-react-lite'
import React, { FC } from 'react'
import { useEntityStore } from 'store/context'
import Outfit from 'store/entities/Outfit/Outfit.store'
import styled, { FlattenSimpleInterpolation, css } from 'styled-components'
import { granite } from 'uiComponents/constants/colors'
import { fsS, fsXS } from 'uiComponents/constants/typography'
import Button from '../styleguide/Button'

interface IProps {
  id: string
  outfit?: Outfit
  hasCta?: boolean
  showIdolImage?: boolean
  customStyles?: {
    outfitTitle?: FlattenSimpleInterpolation
    detailsWrapper?: FlattenSimpleInterpolation
    idolName?: FlattenSimpleInterpolation
  }
  imageSize?: number
  pageType: TrackingPageTypeWithoutId | null
}

const DEFAULT_IMAGE_SIZE = 600

const DetailsWrapper = styled.div<{
  customStyles?: FlattenSimpleInterpolation
}>`
  display: flex;
  padding: 10px;
  min-height: 60px;
  text-align: left;
  overflow: hidden;

  ${({ customStyles }) => customStyles && customStyles}
`

const IdolName = styled.div<{
  $isBig?: boolean
  customStyles?: FlattenSimpleInterpolation
}>`
  letter-spacing: 0.8px;
  text-align: left;

  ${({ $isBig }) =>
    $isBig
      ? css`
          font-size: ${fsS}px;
          line-height: 1.54;
        `
      : css`
          font-size: ${fsXS}px;
          line-height: 1.6;
        `}

  ${({ customStyles }) => customStyles && customStyles}
`

const TextWrapper = styled.div`
  flex: 1;
  text-align: left;
  max-width: 100%;
`

const LinkWrapper = styled.div`
  position: relative;
`

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
`

const Image = styled.img`
  display: block;
  width: 100%;
  background-size: cover;
`

const OutfitTitle = styled.div<{ customStyles?: FlattenSimpleInterpolation }>`
  font-size: ${fsS}px;
  letter-spacing: 0.8px;
  line-height: 1.6;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ customStyles }) => customStyles && customStyles}
`

const OutfitSmall = styled.div`
  color: ${granite};
  font-size: ${fsXS}px;
  letter-spacing: 0.8px;
  line-height: normal;
`

const StyledButton = styled(Button)`
  bottom: 10px;
  left: 10px;
  max-width: calc(100% - 20px);
  position: absolute;
  opacity: 1;
`
const OutfitTile: FC<IProps> = props => {
  const entityStore = useEntityStore()
  const outfit = useObserver(() => entityStore.outfits[props.id])
  const { t } = useTranslation()

  const { hasCta, showIdolImage, imageSize } = props

  if (!outfit) {
    return null
  }

  const imageHash = outfit.hotspotImageHash
    ? outfit.hotspotImageHash
    : outfit.idol.imageHash

  const imageUrl = getImageUrl(imageHash, {
    trim: false,
    width: imageSize || DEFAULT_IMAGE_SIZE,
    height: imageSize || DEFAULT_IMAGE_SIZE,
    bg: 'f2f2f2',
  })

  if (!outfit.idol) {
    return null
  }

  return (
    <ElementTrackingContextProvider>
      <LinkWrapper data-testid="OutfitTile">
        <OutfitPromotionTracker cachePageImpression outfitId={outfit.id}>
          <PromotionLink to={outfit.url}>
            <ImageWrapper>
              <Image
                src={imageUrl}
                alt={`${outfit.idol.name} - ${outfit.title}`}
                {...({ loading: 'lazy', importance: 'low' } as any)}
              />
              {hasCta && (
                <StyledButton url={outfit.url} isGlassy={true}>
                  {t('Shoppe das Outfit')}
                </StyledButton>
              )}
            </ImageWrapper>
            <DetailsWrapper customStyles={props.customStyles?.detailsWrapper}>
              {showIdolImage ? (
                <TextWrapper>
                  <IdolName
                    $isBig={true}
                    customStyles={props.customStyles?.idolName}
                  >
                    {outfit.idol.name}
                  </IdolName>
                  <OutfitSmall>{outfit.title}</OutfitSmall>
                </TextWrapper>
              ) : (
                <TextWrapper>
                  <IdolName customStyles={props.customStyles?.idolName}>
                    {outfit.idol.name}
                  </IdolName>
                  <OutfitTitle customStyles={props.customStyles?.outfitTitle}>
                    {outfit.title}
                  </OutfitTitle>
                </TextWrapper>
              )}
            </DetailsWrapper>
          </PromotionLink>
        </OutfitPromotionTracker>
      </LinkWrapper>
    </ElementTrackingContextProvider>
  )
}

export default OutfitTile
